"use client";

import dynamic from "next/dynamic";
import { useMemo } from "react";
import { ChartCard } from "@/components/ui/chart-card";
import { ExportCsvButton } from "@/components/ui/export-csv-button";
import { AppEmbedButton } from "@/components/embed/AppEmbedButton";
import { RangeToggle } from "@/components/ui/range-toggle";
import { useAppCore } from "@/hooks/use-app-core";
import { useAppPriceHistory } from "@/hooks/use-app-price-history";
import { calculateDateTicks, formatIsoDay, formatTooltipLabel, spansMoreThanAYear } from "@/lib/app-detail/chart-utils";
import { formatPrice, priceCardState, type PriceChange, type PriceSegment } from "@/lib/app-detail/price-history";
import { useUrlState } from "@/hooks/use-url-state";
import { rangeParam } from "@/lib/url-state";
import { csvFileName, type CsvColumn } from "@/lib/export/csv";
import { formatExactDate } from "@/lib/app-detail-utils";
import { getCountryName } from "@/lib/countries";

// recharts is heavy and browser-only; same on-demand pattern as app-ratings-history.tsx.
const CartesianGrid = dynamic(() => import("recharts").then((m) => m.CartesianGrid), { ssr: false }) as typeof import("recharts").CartesianGrid;
const Line = dynamic(() => import("recharts").then((m) => m.Line), { ssr: false }) as typeof import("recharts").Line;
const LineChart = dynamic(() => import("recharts").then((m) => m.LineChart), { ssr: false }) as typeof import("recharts").LineChart;
const ResponsiveContainer = dynamic(() => import("recharts").then((m) => m.ResponsiveContainer), { ssr: false }) as typeof import("recharts").ResponsiveContainer;
const Tooltip = dynamic(() => import("recharts").then((m) => m.Tooltip), { ssr: false }) as typeof import("recharts").Tooltip;
const XAxis = dynamic(() => import("recharts").then((m) => m.XAxis), { ssr: false }) as typeof import("recharts").XAxis;
const YAxis = dynamic(() => import("recharts").then((m) => m.YAxis), { ssr: false }) as typeof import("recharts").YAxis;

// The page's shared ?range=, the same one the rank, estimates and ratings charts read.
const RANGE_PARAM = rangeParam("range", "all");
const SUBTITLE = "Every price this app has charged in this storefront, from the price fetches we hold. Shown in the storefront's own currency and never converted.";
const EMPTY_TEXT = "No price data for this storefront yet.";

type PriceCsvRow = { date: string; row_type: "change" | "first_seen"; price_before?: number; price_after: number; percent?: number | null };

/** One file: the `first_seen` price (in `price_after`), then every change, oldest first. */
export function priceCsvRows(segments: readonly PriceSegment[], changes: readonly PriceChange[]): PriceCsvRow[] {
  const first: PriceCsvRow[] = segments.length ? [{ date: segments[0].from, row_type: "first_seen", price_after: segments[0].price }] : [];
  return [...first, ...changes.map((c) => ({ date: c.date, row_type: "change" as const, price_before: c.before, price_after: c.after, percent: c.percent }))];
}

// The file is named after the app, and the name is a /core read: only the app page, which has the button, makes it.
function PriceCsvExport({ appId, country, range, rows, columns, disabled }: { appId: number; country: string; range: string; rows: readonly PriceCsvRow[]; columns: CsvColumn<PriceCsvRow>[]; disabled: boolean }) {
  const appName = useAppCore(appId).data?.name;
  return <ExportCsvButton rows={rows} columns={columns} fileName={csvFileName("price-history", [appName, appId, country, range])} disabled={disabled} />;
}

/**
 * What this app has cost, storefront by storefront. The change list is the
 * feature and the step chart is the picture of it; an app that was free on
 * every fetch we hold renders no card at all rather than a flat chart at zero.
 */
export function AppPriceHistory({ appId, country, className, variant = "main" }: { appId: number; country: string; className?: string; variant?: "main" | "embed" }) {
  const [range, setRange] = useUrlState(RANGE_PARAM);
  const { data, isLoading, isFetching, error } = useAppPriceHistory(appId, country, range);
  // The whole history, whatever the range shows: the header pill holds this key already, so it is a cache read.
  const ever = useAppPriceHistory(appId, country, "all");
  const { segments, changes, meta } = data;
  const csvRows = useMemo(() => priceCsvRows(segments, changes), [segments, changes]);

  // A step line needs the price at every boundary plus the day the last segment ends.
  const points = useMemo(() => {
    const rows = segments.map((s) => ({ date: s.from, price: s.price }));
    const last = segments[segments.length - 1];
    if (last && last.to !== last.from) rows.push({ date: last.to, price: last.price });
    return rows;
  }, [segments]);
  const withYear = spansMoreThanAYear(points);

  const title = `Price history · ${getCountryName(country) ?? country.toUpperCase()}`;
  const state = priceCardState({ rangedError: !!error, everError: !!ever.error, everLoading: ever.isLoading, everFree: ever.data.meta.free });
  // The app page drops the card for an app that was always free; an embed has nothing to drop it into.
  if (state === "hidden") {
    if (variant === "main") return null;
    return (
      <ChartCard title={title} terminalCode="APP.PRICE" className={className}>
        <p className="py-6 text-lg font-semibold" data-testid="price-history-free">Free</p>
      </ChartCard>
    );
  }
  if (state === "loading") return <ChartCard title={title} terminalCode="APP.PRICE" isLoading className={className} />;

  const csvColumns: CsvColumn<PriceCsvRow>[] = [
    { key: "app_store_id", value: () => appId },
    { key: "country", value: () => country },
    "date", "row_type", "price_before", "price_after", "percent",
    { key: "currency", value: () => meta.currency },
    { key: "has_iap", value: () => meta.iap },
    { key: "price_fetched_at", value: () => meta.latestAt },
  ];
  const action = (
    <div className="flex items-center gap-1.5">
      <RangeToggle value={range} onChange={setRange} />
      {/* An embed has no sign-in, which the CSV needs, so it gets neither button. */}
      {variant === "main" && (
        <>
          <PriceCsvExport appId={appId} country={country} range={range} rows={csvRows} columns={csvColumns} disabled={isLoading || isFetching} />
          <AppEmbedButton type="price-history" appId={appId} country={country} />
        </>
      )}
    </div>
  );

  const footer = (
    <p className="mt-2 text-[11px] text-muted-foreground">
      {meta.iap === true ? "Offers in-app purchases. " : meta.iap === false ? "No in-app purchases. " : ""}
      {meta.currencyAssumed && segments.length > 0 ? `${meta.currency} assumed: we hold no confirmed currency for this storefront. ` : ""}
      {meta.latestAt ? `Price fetched ${formatExactDate(meta.latestAt, { utc: true })}.` : ""}</p>
  );

  return (
    <ChartCard title={title} subtitle={SUBTITLE} terminalCode="APP.PRICE" action={action} isLoading={isLoading} className={className}>
      {state === "error" ? (
        <p className="flex h-32 items-center justify-center text-sm text-red-500" role="alert" data-testid="price-history-error">The price history could not be loaded, so this card is empty.</p>
      ) : segments.length === 0 ? (
        <p className="flex h-32 items-center justify-center text-sm text-muted-foreground">{EMPTY_TEXT}</p>
      ) : meta.free ? (
        <p className="py-6 text-lg font-semibold" data-testid="price-history-free">Free</p>
      ) : (
        <>
          <div className="h-32">
            <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 1, height: 1 }}>
              <LineChart data={points} margin={{ top: 6, right: 0, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="#333" strokeDasharray="3 3" strokeOpacity={0.4} />
                <XAxis dataKey="date" tickLine={false} axisLine={false} tickMargin={8} tick={{ fill: "#999", fontSize: 10 }} ticks={calculateDateTicks(points, 4)} tickFormatter={(iso: string) => formatIsoDay(iso, withYear)} />
                <YAxis width={52} tickLine={false} axisLine={false} tick={{ fill: "#999", fontSize: 10 }} tickFormatter={(v: number) => formatPrice(v, meta.currency)} />
                <Tooltip content={({ active, payload, label }) => active && payload && payload.length ? (
                  <div className="rounded-md border bg-background p-2 text-xs shadow-md">
                    <div className="text-muted-foreground">{formatTooltipLabel(String(label))}</div>
                    <div className="font-medium">{formatPrice(Number(payload[0].value), meta.currency)}</div>
                  </div>
                ) : null} />
                <Line type="stepAfter" dataKey="price" stroke="var(--accent-primary, #10b981)" strokeWidth={2} dot={{ r: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <ul className="mt-3 space-y-1 text-xs" data-testid="price-history-changes">
            {[...changes].reverse().map((c) => (
              <li key={c.date} className="flex items-center gap-2">
                <span className="w-32 shrink-0 whitespace-nowrap text-muted-foreground">{formatExactDate(`${c.date}T00:00:00Z`, { utc: true })}</span>
                <span className="tabular-nums">{formatPrice(c.before, meta.currency)} → <span className="font-medium">{formatPrice(c.after, meta.currency)}</span></span>
                {c.percent !== null && <span className={c.percent < 0 ? "text-emerald-500" : "text-red-500"}>{c.percent > 0 ? "+" : ""}{c.percent}%</span>}
              </li>
            ))}
            {/* On a narrowed range this is the first price INSIDE the window, not the first we hold. */}
            <li className="flex items-center gap-2">
              <span className="w-32 shrink-0 whitespace-nowrap text-muted-foreground">{formatExactDate(`${segments[0].from}T00:00:00Z`, { utc: true })}</span>
              <span className="text-muted-foreground">{range === "all" ? "first seen at" : "first in this range at"} {formatPrice(segments[0].price, meta.currency)}</span></li>
          </ul>
          {footer}
        </>
      )}
    </ChartCard>
  );
}
